import { observer } from 'mobx-react-lite';
import React, { useEffect, useState, useMemo } from 'react';
import { Grid, Header, Modal, Tab } from 'semantic-ui-react';
import PaginatedTable from '../../../app/common/table/PaginatedTable';
import DateHelper from '../../../app/helpers/dateHelper';
import { FilterValues } from '../../../app/models/common/filterValues';
import { DailyReport } from '../../../app/models/employeeDailyReport';
import { useStore } from '../../../app/stores/store';
import { FaRegEye } from 'react-icons/fa';
import { Tooltip, makeStyles } from '@material-ui/core';

const dateHelper = new DateHelper();

interface Props {
    employeeId: string;
}

interface GroupedDailyReport extends DailyReport {
    isDateHeader?: boolean;
    dailyTotal?: number;
}

const useStyles = makeStyles({
    tooltip: {
        backgroundColor: 'white !important',
        color: 'black !important',
        fontSize: '16px !important',
        border: '1px solid #dadde9',
        maxWidth: '800px !important',
        maxHeight: '600px !important',
        padding: '12px 16px !important',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        '& > p': {
            margin: 0,
            whiteSpace: 'pre-wrap',
            lineHeight: '1.5',
            fontSize: '16px',
            maxHeight: '550px',
            overflowY: 'auto'
        }
    }
});

const EmployeeDailyReports = ({ employeeId }: Props) => {
    const classes = useStyles();
    const { employeeStore, modalStore } = useStore();
    const { isDailyReportLoaded, dailyReports, dailyReportsTotal, loadEmployeeDailyReports } = employeeStore;

    const [filterValues, setFilterValues] = useState<FilterValues>({
        id: employeeId,
        pageSize: 20,
        pageNumber: 1,
        searchKeyword: ""
    });

    const handleOnChangePage = (page: number) => {
        setFilterValues({ ...filterValues, pageNumber: page });
    };

    // Process data to create visual grouping
    const processDataForGrouping = (data: DailyReport[]): GroupedDailyReport[] => {
        if (!data) return [];
        
        const processedData: GroupedDailyReport[] = [];
        let currentDate = '';
        let dailyTotal = 0;
        let currentGroup: GroupedDailyReport[] = [];

        data.forEach((report, index) => {
            const reportDate = dateHelper.formatShortDate(report.date.toString());
            
            if (currentDate !== reportDate) {
                // Add the previous group if it exists
                if (currentGroup.length > 0) {
                    // Only add total row if there's more than one entry for the date
                    if (currentGroup.length > 1) {
                        processedData.push(...currentGroup);
                        processedData.push({
                            ...currentGroup[0],
                            isDateHeader: true,
                            project: '',
                            summary: 'Total Hours',
                            hours: Number(dailyTotal.toFixed(2))
                        });
                    } else {
                        // For single entries, just add the entry itself
                        processedData.push(...currentGroup);
                    }
                }
                
                // Start a new group
                currentDate = reportDate;
                currentGroup = [];
                dailyTotal = 0;
            }
            
            currentGroup.push(report);
            dailyTotal += Number(report.hours);

            // Handle the last group
            if (index === data.length - 1) {
                if (currentGroup.length > 1) {
                    processedData.push(...currentGroup);
                    processedData.push({
                        ...currentGroup[0],
                        isDateHeader: true,
                        project: '',
                        summary: 'Total Hours',
                        hours: dailyTotal
                    });
                } else {
                    // For single entries, just add the entry itself
                    processedData.push(...currentGroup);
                }
            }
        });

        return processedData;
    };

    // Memoize the processed data
    const processedData = useMemo(() => 
        processDataForGrouping(dailyReports || []),
        [dailyReports] // Only recompute when dailyReports changes
    );

    // Memoize the unique dates and date groups
    const { uniqueDates, dateGroups } = useMemo(() => {
        const dates = [...new Set(
            processedData
                .filter(item => !item.isDateHeader)
                .map(item => dateHelper.formatShortDate(item.date.toString()))
        )];

        const groups = new Map();
        processedData.forEach(item => {
            const date = dateHelper.formatShortDate(item.date.toString());
            if (!groups.has(date)) {
                groups.set(date, []);
            }
            if (!item.isDateHeader) {
                groups.get(date).push(item);
            }
        });

        return { uniqueDates: dates, dateGroups: groups };
    }, [processedData]);

    useEffect(() => {
        loadEmployeeDailyReports(filterValues);
    }, [filterValues, loadEmployeeDailyReports]);

    return (
        <Tab.Pane>
            <Grid divided="vertically">
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Header>Daily Report</Header>
                        <PaginatedTable
                            columns={[
                                {
                                    title: "Date",
                                    field: "date",
                                    render: (data: GroupedDailyReport) => {
                                        if (data.isDateHeader) return "";
                                        
                                        const currentDate = dateHelper.formatShortDate(data.date.toString());
                                        const dateGroup = dateGroups.get(currentDate) || [];

                                        if (dateGroup.length === 1) {
                                            return currentDate;
                                        }

                                        const middleIndex = Math.floor(dateGroup.length / 2);
                                        const isMiddleRow = dateGroup[middleIndex] === data;

                                        return isMiddleRow ? currentDate : "";
                                    }
                                },
                                { 
                                    title: "Project", 
                                    field: "project"
                                },
                                { 
                                    title: "Summary", 
                                    field: "summary",
                                    width: '600px',
                                    render: (data: GroupedDailyReport) => {
                                        if (data.isDateHeader) return data.summary;
                                        return (
                                            <div style={{ display: 'inline-block' }}>
                                                <Tooltip 
                                                    title={<p>{data.summary}</p>}
                                                    classes={{ tooltip: classes.tooltip }}
                                                    placement="top"
                                                    arrow
                                                    interactive
                                                >
                                                    <div 
                                                        style={{
                                                            maxWidth: '750px',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: 'inline-block'
                                                        }}
                                                    >
                                                        {data.summary}
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        );
                                    }
                                },
                                { 
                                    title: "Hours", 
                                    field: "hours",
                                    align: 'right',
                                    headerStyle: { textAlign: 'right' },
                                    cellStyle: { textAlign: 'right', paddingRight: '30px' },
                                    render: (data: GroupedDailyReport) => {
                                        if (data.isDateHeader) return <strong>{data.hours}</strong>;
                                        
                                        const currentDate = dateHelper.formatShortDate(data.date.toString());
                                        const dateGroup = dateGroups.get(currentDate) || [];
                                        
                                        return dateGroup.length === 1 ? 
                                            <strong>{data.hours}</strong> : 
                                            data.hours;
                                    }
                                }
                            ]}
                            data={processedData}
                            total={dailyReportsTotal}
                            onChangePage={handleOnChangePage}
                            pageSize={20}
                            pageNumber={filterValues.pageNumber}
                            isLoading={!isDailyReportLoaded}
                            style={{ boxShadow: "none", padding: 0, marginTop: "30px" }}
                            sorting={false}
                            rowStyle={(rowData: GroupedDailyReport, index: number) => {
                                const currentDate = dateHelper.formatShortDate(rowData.date.toString());
                                const dateGroup = dateGroups.get(currentDate) || [];
                                
                                // If there's only one row for this date, use white background
                                if (dateGroup.length === 1 && !rowData.isDateHeader) {
                                    return { backgroundColor: '#fff', padding: '0 20px' };
                                }
                                
                                const dateIndex = uniqueDates.indexOf(currentDate);
                                const style = {
                                    backgroundColor: dateIndex % 2 === 0 ? '#fff' : '#f3f9ff',
                                    padding: '0 20px'
                                };

                                // Add top border only for the total row
                                if (rowData.isDateHeader) {
                                    return {
                                        ...style,
                                        borderTop: '1px solid #eee'
                                    };
                                }

                                return style;
                            }}
                        />
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </Tab.Pane>
    );
}

export default observer(EmployeeDailyReports);